import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRatingsResumenStore } from '../../../store/ratings/thunks';
import { Box, Typography, Card, Tabs, Tab, Grid, Divider } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import {
  Assignment as AssignmentIcon,
  StarOutline as StarOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  SentimentVerySatisfied as SentimentVerySatisfiedIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

import { TabResumen } from './components/TabResumen';
import { TabEncuestas } from './components/TabEncuestas';
import { TabPreguntas } from './components/TabPreguntas';
import { TabMetricas } from './components/TabMetricas';
import { TabReglas } from './components/TabReglas';
import { TabSLA } from './components/TabSLA';
import { TabHallazgos } from './components/TabHallazgos';
import { BreadCrumbsAndTitle } from '../../../components/generics/BreadCrumbsAndTitle';
import { alertInformativo } from '../../../commons/texts';
import { AlertsInformativos } from '../../../components/generics/AlertsInformativos';
import { mtDividerUpTable } from '../../../commons';

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.85rem',
  minWidth: 'auto',
  marginRight: theme.spacing(3),
  padding: theme.spacing(1.5, 0),
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

export const Resumen = () => {
  const dispatch = useDispatch();
  const { resumen } = useSelector((state: any) => state.ratingsDevices);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    dispatch(getRatingsResumenStore());
  }, [dispatch]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const MetricCard = ({
    title,
    value,
    icon,
    iconBgColor,
    trendText,
    isPositiveTrend,
    trendActive = false,
    additionalValue,
  }: any) => {
    const trendColor = isPositiveTrend ? '#34A853' : '#EA4335';

    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
            <Box
              sx={{
                bgcolor: iconBgColor,
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                {title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                <Typography variant="h4" fontWeight="800" sx={{ color: '#2e263d' }}>
                  {value}
                </Typography>
                {additionalValue && (
                  <Typography variant="subtitle1" color="text.secondary" fontWeight="600">
                    {additionalValue}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          {trendActive && trendText && (
            <Box mt="auto" display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="caption"
                sx={{
                  color: trendColor,
                  fontWeight: 'bold',
                  bgcolor: alpha(trendColor, 0.1),
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {trendText}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: 'auto', minHeight: '100vh' }}>
      {/* HEADER */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <BreadCrumbsAndTitle
            title="Resumen"
            mainModule="Calificaciones"
            action=""
            parent="Resumen"
            icon="Assessment"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <AlertsInformativos
              type={'info'}
              title={'info'}
              content={alertInformativo.resumenCalificaciones}
            />
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      {/* KPI CARDS */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="Encuestas activas"
            value={resumen?.activeSurveysCount ?? 0}
            icon={<AssignmentIcon />}
            iconBgColor="#4A148C"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="Respuestas este mes"
            value={resumen?.responses?.thisMonthCount ?? 0}
            icon={<CheckCircleOutlineIcon />}
            iconBgColor="#5DCC0A"
            trendActive={resumen?.responses?.vsPercentage !== undefined}
            isPositiveTrend={resumen?.responses?.vsPreviousMonth !== 'bajo'}
            trendText={
              resumen?.responses?.vsPercentage !== undefined
                ? `${resumen.responses.vsPreviousMonth === 'bajo' ? '-' : '+'}${resumen.responses.vsPercentage}% vs mes anterior`
                : null
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="NPS promedio"
            value={
              resumen?.averages?.nps !== null && resumen?.averages?.nps !== undefined
                ? resumen.averages.nps > 0
                  ? `+${resumen.averages.nps}`
                  : resumen.averages.nps
                : '-'
            }
            icon={<SentimentVerySatisfiedIcon />}
            iconBgColor="#2196F3"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="CSAT promedio"
            value={
              resumen?.averages?.csat !== null && resumen?.averages?.csat !== undefined
                ? `${resumen.averages.csat}%`
                : '-'
            }
            icon={<EmojiEmotionsIcon />}
            iconBgColor="#9C27B0"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="CES promedio"
            value={
              resumen?.averages?.ces !== null && resumen?.averages?.ces !== undefined
                ? resumen.averages.ces
                : '-'
            }
            additionalValue={
              resumen?.averages?.ces !== null && resumen?.averages?.ces !== undefined ? '/ 5' : null
            }
            icon={<SpeedIcon />}
            iconBgColor="#FFB400"
          />
        </Grid>
      </Grid>

      {/* TABS NAVEGACIÓN */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 48 }}
        >
          <StyledTab
            label="Resumen"
            iconPosition="start"
            icon={<AssignmentIcon fontSize="small" />}
          />
          <StyledTab label="Encuestas" />
          {/* <StyledTab label="Preguntas" /> */}
          <StyledTab label="Métricas (NPS, CSAT, CES)" />
          <StyledTab label="Reglas y Alertas" />
          {/* <StyledTab label="SLA" /> */}
          <StyledTab label="Hallazgos Automáticos" />
        </Tabs>
      </Box>

      {/* TAB CONTENT */}
      {tabValue === 0 && <TabResumen resumen={resumen} />}
      {tabValue === 1 && <TabEncuestas resumen={resumen} />}
      {/* {tabValue === 2 && <TabPreguntas resumen={resumen} />} */}
      {tabValue === 2 && <TabMetricas resumen={resumen} />}
      {tabValue === 3 && <TabReglas resumen={resumen} />}
      {/* {tabValue === 4 && <TabSLA resumen={resumen} />} */}
      {tabValue === 4 && <TabHallazgos resumen={resumen} />}
    </Box>
  );
};
